import { lazy } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';

import GalleryPage from '../pages/gallery/page';
import GalleryAdminLoginPage from '../pages/gallery/admin/login/page';
import GalleryAdmin from '../pages/gallery/admin/page';
import EnquiriesAdminLoginPage from '../pages/admin/enquiries/login/page';
import EnquiriesAdminPage from '../pages/admin/enquiries/page';

const HomePage = lazy(() => import('../pages/home/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));
const InstallationsPage = lazy(() => import('../pages/services/installations/page'));
const ElectricalInstallationsPage = lazy(() => import('../pages/services/installations/electrical-installations/page'));
const RewiringPage = lazy(() => import('../pages/services/installations/rewiring/page'));
const ConsumerUnitUpgradesPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/page'));
const LightingInstallationPage = lazy(() => import('../pages/services/installations/lighting-installation/page'));
const LEDLightingPage = lazy(() => import('../pages/services/installations/led-lighting/page'));
const OutdoorGardenLightingPage = lazy(() => import('../pages/services/installations/outdoor-garden-lighting/page'));
const SecurityLightingPage = lazy(() => import('../pages/services/installations/security-lighting/page'));
const SocketSwitchPage = lazy(() => import('../pages/services/installations/socket-switch/page'));
const EVChargerPage = lazy(() => import('../pages/services/installations/ev-charger/page'));
const SmartHomePage = lazy(() => import('../pages/services/installations/smart-home/page'));
const HomeAutomationPage = lazy(() => import('../pages/services/installations/home-automation/page'));
const HeatingControlsPage = lazy(() => import('../pages/services/installations/heating-controls/page'));
const ElectricShowerPage = lazy(() => import('../pages/services/installations/electric-shower/page'));
const KitchenElectricalPage = lazy(() => import('../pages/services/installations/kitchen-electrical/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));

const RepairsPage = lazy(() => import('../pages/services/repairs/page'));
const ElectricalRepairsPage = lazy(() => import('../pages/services/repairs/electrical-repairs/page'));
const FaultFindingPage = lazy(() => import('../pages/services/repairs/fault-finding/page'));
const TrippingCircuitsPage = lazy(() => import('../pages/services/repairs/tripping-circuits/page'));
const FuseBoxRepairsPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/page'));
const LightingRepairsPage = lazy(() => import('../pages/services/repairs/lighting-repairs/page'));
const SocketSwitchRepairsPage = lazy(() => import('../pages/services/repairs/socket-switch-repairs/page'));
const EmergencyCalloutsPage = lazy(() => import('../pages/services/repairs/emergency-callouts/page'));
const MaintenanceContractsPage = lazy(() => import('../pages/services/repairs/maintenance-contracts/page'));
const PATTestingPage = lazy(() => import('../pages/services/repairs/pat-testing/page'));
const ApplianceRepairsPage = lazy(() => import('../pages/services/repairs/appliance-repairs/page'));

// Fuse Box Repairs Area Pages
const FuseBoxRepairsAbbotsLangleyPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/abbots-langley/page'));
const FuseBoxRepairsBorehamwoodPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/borehamwood/page'));
const FuseBoxRepairsBusheyPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/bushey/page'));
const FuseBoxRepairsChorleywoodPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/chorleywood/page'));
const FuseBoxRepairsCroxleyGreenPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/croxley-green/page'));
const FuseBoxRepairsEdgwarePage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/edgware/page'));
const FuseBoxRepairsElstreePage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/elstree/page'));
const FuseBoxRepairsHarrowPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/harrow/page'));
const FuseBoxRepairsMillHillPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/mill-hill/page'));
const FuseBoxRepairsMoorParkNorthwoodPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/moor-park-northwood/page'));
const FuseBoxRepairsPinnerPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/pinner/page'));
const FuseBoxRepairsPottersBarPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/potters-bar/page'));
const FuseBoxRepairsRadlettPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/radlett/page'));
const FuseBoxRepairsRickmansworthPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/rickmansworth/page'));
const FuseBoxRepairsRuislipPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/ruislip/page'));
const FuseBoxRepairsSouthOxheyPage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/south-oxhey/page'));
const FuseBoxRepairsStanmorePage = lazy(() => import('../pages/services/repairs/fuse-box-repairs/areas/stanmore/page'));

const SafetyCompliancePage = lazy(() => import('../pages/services/safety-compliance/page'));
const EICRTestingPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/page'));

// EICR Testing Area Pages
const EICRWatfordTownCentrePage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/watford-town-centre/page'));
const EICROxheyPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/oxhey/page'));
const EICRBusheyPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/bushey/page'));
const EICRCroxleyGreenPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/croxley-green/page'));
const EICRAbbotsLangleyPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/abbots-langley/page'));
const EICRRickmansworthPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/rickmansworth/page'));
const EICRGarstonPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/garston/page'));
const EICRNorthWatfordPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/north-watford/page'));
const EICRWestWatfordPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/west-watford/page'));
const EICRCarpendersParkPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/carpenders-park/page'));
const EICRLeavesdenPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/leavesden/page'));
const EICRNascotWoodPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/nascot-wood/page'));
const EICRHolywellPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/holywell/page'));
const EICRMeridenPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/meriden/page'));
const EICRCassioburyPage = lazy(() => import('../pages/services/safety-compliance/eicr-testing/areas/cassiobury/page'));

const LandlordSafetyCertificatesPage = lazy(() => import('../pages/services/safety-compliance/landlord-safety-certificates/page'));
const HomebuyerElectricalReportsPage = lazy(() => import('../pages/services/safety-compliance/homebuyer-electrical-reports/page'));
const SmokeHeatAlarmPage = lazy(() => import('../pages/services/safety-compliance/smoke-heat-alarm/page'));
const FireAlarmSystemsPage = lazy(() => import('../pages/services/safety-compliance/fire-alarm-systems/page'));
const SurgeProtectionPage = lazy(() => import('../pages/services/safety-compliance/surge-protection/page'));
const EarthingBondingPage = lazy(() => import('../pages/services/safety-compliance/earthing-bonding/page'));
const RCDTestingPage = lazy(() => import('../pages/services/safety-compliance/rcd-testing/page'));

// Area Pages
const RickmansworthPage = lazy(() => import('../pages/areas/electrician-rickmansworth/page'));
const ChorleywoodPage = lazy(() => import('../pages/areas/electrician-chorleywood/page'));
const MoorParkNorthwoodPage = lazy(() => import('../pages/areas/electrician-moor-park-northwood/page'));
const CroxleyGreenPage = lazy(() => import('../pages/areas/electrician-croxley-green/page'));
const PinnerPage = lazy(() => import('../pages/areas/electrician-pinner/page'));
const BusheyPage = lazy(() => import('../pages/areas/electrician-bushey/page'));
const AbbotsLangleyPage = lazy(() => import('../pages/areas/electrician-abbots-langley/page'));
const HarrowPage = lazy(() => import('../pages/areas/electrician-harrow/page'));
const RuislipPage = lazy(() => import('../pages/areas/electrician-ruislip/page'));
const StanmorePage = lazy(() => import('../pages/areas/electrician-stanmore/page'));
const EdgwarePage = lazy(() => import('../pages/areas/electrician-edgware/page'));
const MillHillPage = lazy(() => import('../pages/areas/electrician-mill-hill/page'));
const RadlettPage = lazy(() => import('../pages/areas/electrician-radlett/page'));
const BorehamwoodPage = lazy(() => import('../pages/areas/electrician-borehamwood/page'));
const ElstreePage = lazy(() => import('../pages/areas/electrician-elstree/page'));
const PottersBarPage = lazy(() => import('../pages/areas/electrician-potters-bar/page'));
const SouthOxheyPage = lazy(() => import('../pages/areas/electrician-south-oxhey/page'));

// Electrical Installations Area Pages
const EIRickmansworthPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/rickmansworth/page'));
const EIChorleywoodPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/chorleywood/page'));
const EIMoorParkNorthwoodPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/moor-park-northwood/page'));
const EICroxleyGreenPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/croxley-green/page'));
const EIPinnerPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/pinner/page'));
const EIBusheyPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/bushey/page'));
const EIAbbotsLangleyPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/abbots-langley/page'));
const EIHarrowPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/harrow/page'));
const EIRuislipPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/ruislip/page'));
const EIStanmorePage = lazy(() => import('../pages/services/installations/electrical-installations/areas/stanmore/page'));
const EIEdgwarePage = lazy(() => import('../pages/services/installations/electrical-installations/areas/edgware/page'));
const EIMillHillPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/mill-hill/page'));
const EIRadlettPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/radlett/page'));
const EIBorehamwoodPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/borehamwood/page'));
const EIElstreePage = lazy(() => import('../pages/services/installations/electrical-installations/areas/elstree/page'));
const EIPottersBarPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/potters-bar/page'));
const EISouthOxheyPage = lazy(() => import('../pages/services/installations/electrical-installations/areas/south-oxhey/page'));

// Rewiring Area Pages
const RewiringRickmansworthPage = lazy(() => import('../pages/services/installations/rewiring/areas/rickmansworth/page'));
const RewiringChorleywoodPage = lazy(() => import('../pages/services/installations/rewiring/areas/chorleywood/page'));
const RewiringMoorParkNorthwoodPage = lazy(() => import('../pages/services/installations/rewiring/areas/moor-park-northwood/page'));
const RewiringCroxleyGreenPage = lazy(() => import('../pages/services/installations/rewiring/areas/croxley-green/page'));
const RewiringPinnerPage = lazy(() => import('../pages/services/installations/rewiring/areas/pinner/page'));
const RewiringBusheyPage = lazy(() => import('../pages/services/installations/rewiring/areas/bushey/page'));
const RewiringAbbotsLangleyPage = lazy(() => import('../pages/services/installations/rewiring/areas/abbots-langley/page'));
const RewiringHarrowPage = lazy(() => import('../pages/services/installations/rewiring/areas/harrow/page'));
const RewiringRuislipPage = lazy(() => import('../pages/services/installations/rewiring/areas/ruislip/page'));
const RewiringStanmorePage = lazy(() => import('../pages/services/installations/rewiring/areas/stanmore/page'));
const RewiringEdgwarePage = lazy(() => import('../pages/services/installations/rewiring/areas/edgware/page'));
const RewiringMillHillPage = lazy(() => import('../pages/services/installations/rewiring/areas/mill-hill/page'));
const RewiringRadlettPage = lazy(() => import('../pages/services/installations/rewiring/areas/radlett/page'));
const RewiringBorehamwoodPage = lazy(() => import('../pages/services/installations/rewiring/areas/borehamwood/page'));
const RewiringElstreePage = lazy(() => import('../pages/services/installations/rewiring/areas/elstree/page'));
const RewiringPottersBarPage = lazy(() => import('../pages/services/installations/rewiring/areas/potters-bar/page'));
const RewiringSouthOxheyPage = lazy(() => import('../pages/services/installations/rewiring/areas/south-oxhey/page'));

const EVChargerRickmansworthPage = lazy(() => import('../pages/services/installations/ev-charger/areas/rickmansworth/page'));
const EVChargerChorleywoodPage = lazy(() => import('../pages/services/installations/ev-charger/areas/chorleywood/page'));
const EVChargerMoorParkNorthwoodPage = lazy(() => import('../pages/services/installations/ev-charger/areas/moor-park-northwood/page'));
const EVChargerCroxleyGreenPage = lazy(() => import('../pages/services/installations/ev-charger/areas/croxley-green/page'));
const EVChargerPinnerPage = lazy(() => import('../pages/services/installations/ev-charger/areas/pinner/page'));
const EVChargerBusheyPage = lazy(() => import('../pages/services/installations/ev-charger/areas/bushey/page'));
const EVChargerAbbotsLangleyPage = lazy(() => import('../pages/services/installations/ev-charger/areas/abbots-langley/page'));
const EVChargerHarrowPage = lazy(() => import('../pages/services/installations/ev-charger/areas/harrow/page'));
const EVChargerRuislipPage = lazy(() => import('../pages/services/installations/ev-charger/areas/ruislip/page'));
const EVChargerStanmorePage = lazy(() => import('../pages/services/installations/ev-charger/areas/stanmore/page'));
const EVChargerEdgwarePage = lazy(() => import('../pages/services/installations/ev-charger/areas/edgware/page'));
const EVChargerMillHillPage = lazy(() => import('../pages/services/installations/ev-charger/areas/mill-hill/page'));
const EVChargerRadlettPage = lazy(() => import('../pages/services/installations/ev-charger/areas/radlett/page'));
const EVChargerBorehamwoodPage = lazy(() => import('../pages/services/installations/ev-charger/areas/borehamwood/page'));
const EVChargerElstreePage = lazy(() => import('../pages/services/installations/ev-charger/areas/elstree/page'));
const EVChargerPottersBarPage = lazy(() => import('../pages/services/installations/ev-charger/areas/potters-bar/page'));
const EVChargerSouthOxheyPage = lazy(() => import('../pages/services/installations/ev-charger/areas/south-oxhey/page'));

// Consumer Unit Upgrades Area Pages
const ConsumerUnitChorleywoodPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/chorleywood/page'));
const ConsumerUnitMoorParkNorthwoodPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/moor-park-northwood/page'));
const ConsumerUnitCroxleyGreenPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/croxley-green/page'));
const ConsumerUnitPinnerPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/pinner/page'));
const ConsumerUnitBusheyPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/bushey/page'));
const ConsumerUnitAbbotsLangleyPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/abbots-langley/page'));
const ConsumerUnitHarrowPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/harrow/page'));
const ConsumerUnitRuislipPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/ruislip/page'));
const ConsumerUnitStanmorePage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/stanmore/page'));
const ConsumerUnitEdgwarePage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/edgware/page'));
const ConsumerUnitMillHillPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/mill-hill/page'));
const ConsumerUnitRadlettPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/radlett/page'));
const ConsumerUnitBorehamwoodPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/borehamwood/page'));
const ConsumerUnitElstreePage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/elstree/page'));
const ConsumerUnitPottersBarPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/potters-bar/page'));
const ConsumerUnitSouthOxheyPage = lazy(() => import('../pages/services/installations/consumer-unit-upgrades/areas/south-oxhey/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/gallery',
    element: <GalleryPage />,
  },
  {
    path: '/gallery/admin/login',
    element: <GalleryAdminLoginPage />,
  },
  {
    path: '/gallery/admin',
    element: <GalleryAdmin />,
  },
  {
    path: '/admin/enquiries/login',
    element: <EnquiriesAdminLoginPage />,
  },
  {
    path: '/admin/enquiries',
    element: <EnquiriesAdminPage />,
  },
  {
    path: '/services/installations',
    element: <InstallationsPage />,
  },
  {
    path: '/services/installations/electrical-installations',
    element: <ElectricalInstallationsPage />,
  },
  {
    path: '/services/installations/rewiring',
    element: <RewiringPage />,
  },
  {
    path: '/services/installations/rewiring/rickmansworth',
    element: <RewiringRickmansworthPage />,
  },
  {
    path: '/services/installations/rewiring/chorleywood',
    element: <RewiringChorleywoodPage />,
  },
  {
    path: '/services/installations/rewiring/moor-park-northwood',
    element: <RewiringMoorParkNorthwoodPage />,
  },
  {
    path: '/services/installations/rewiring/croxley-green',
    element: <RewiringCroxleyGreenPage />,
  },
  {
    path: '/services/installations/rewiring/pinner',
    element: <RewiringPinnerPage />,
  },
  {
    path: '/services/installations/rewiring/bushey',
    element: <RewiringBusheyPage />,
  },
  {
    path: '/services/installations/rewiring/abbots-langley',
    element: <RewiringAbbotsLangleyPage />,
  },
  {
    path: '/services/installations/rewiring/harrow',
    element: <RewiringHarrowPage />,
  },
  {
    path: '/services/installations/rewiring/ruislip',
    element: <RewiringRuislipPage />,
  },
  {
    path: '/services/installations/rewiring/stanmore',
    element: <RewiringStanmorePage />,
  },
  {
    path: '/services/installations/rewiring/edgware',
    element: <RewiringEdgwarePage />,
  },
  {
    path: '/services/installations/rewiring/mill-hill',
    element: <RewiringMillHillPage />,
  },
  {
    path: '/services/installations/rewiring/radlett',
    element: <RewiringRadlettPage />,
  },
  {
    path: '/services/installations/rewiring/borehamwood',
    element: <RewiringBorehamwoodPage />,
  },
  {
    path: '/services/installations/rewiring/elstree',
    element: <RewiringElstreePage />,
  },
  {
    path: '/services/installations/rewiring/potters-bar',
    element: <RewiringPottersBarPage />,
  },
  {
    path: '/services/installations/rewiring/south-oxhey',
    element: <RewiringSouthOxheyPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades',
    element: <ConsumerUnitUpgradesPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/chorleywood',
    element: <ConsumerUnitChorleywoodPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/moor-park-northwood',
    element: <ConsumerUnitMoorParkNorthwoodPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/croxley-green',
    element: <ConsumerUnitCroxleyGreenPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/pinner',
    element: <ConsumerUnitPinnerPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/bushey',
    element: <ConsumerUnitBusheyPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/abbots-langley',
    element: <ConsumerUnitAbbotsLangleyPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/harrow',
    element: <ConsumerUnitHarrowPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/ruislip',
    element: <ConsumerUnitRuislipPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/stanmore',
    element: <ConsumerUnitStanmorePage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/edgware',
    element: <ConsumerUnitEdgwarePage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/mill-hill',
    element: <ConsumerUnitMillHillPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/radlett',
    element: <ConsumerUnitRadlettPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/borehamwood',
    element: <ConsumerUnitBorehamwoodPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/elstree',
    element: <ConsumerUnitElstreePage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/potters-bar',
    element: <ConsumerUnitPottersBarPage />,
  },
  {
    path: '/services/installations/consumer-unit-upgrades/south-oxhey',
    element: <ConsumerUnitSouthOxheyPage />,
  },
  {
    path: '/services/installations/lighting-installation',
    element: <LightingInstallationPage />,
  },
  {
    path: '/services/installations/led-lighting',
    element: <LEDLightingPage />,
  },
  {
    path: '/services/installations/outdoor-garden-lighting',
    element: <OutdoorGardenLightingPage />,
  },
  {
    path: '/services/installations/security-lighting',
    element: <SecurityLightingPage />,
  },
  {
    path: '/services/installations/socket-switch',
    element: <SocketSwitchPage />,
  },
  {
    path: '/services/installations/ev-charger',
    element: <EVChargerPage />,
  },
  {
    path: '/services/installations/ev-charger/rickmansworth',
    element: <EVChargerRickmansworthPage />,
  },
  {
    path: '/services/installations/ev-charger/chorleywood',
    element: <EVChargerChorleywoodPage />,
  },
  {
    path: '/services/installations/ev-charger/moor-park-northwood',
    element: <EVChargerMoorParkNorthwoodPage />,
  },
  {
    path: '/services/installations/ev-charger/croxley-green',
    element: <EVChargerCroxleyGreenPage />,
  },
  {
    path: '/services/installations/ev-charger/pinner',
    element: <EVChargerPinnerPage />,
  },
  {
    path: '/services/installations/ev-charger/bushey',
    element: <EVChargerBusheyPage />,
  },
  {
    path: '/services/installations/ev-charger/abbots-langley',
    element: <EVChargerAbbotsLangleyPage />,
  },
  {
    path: '/services/installations/ev-charger/harrow',
    element: <EVChargerHarrowPage />,
  },
  {
    path: '/services/installations/ev-charger/ruislip',
    element: <EVChargerRuislipPage />,
  },
  {
    path: '/services/installations/ev-charger/stanmore',
    element: <EVChargerStanmorePage />,
  },
  {
    path: '/services/installations/ev-charger/edgware',
    element: <EVChargerEdgwarePage />,
  },
  {
    path: '/services/installations/ev-charger/mill-hill',
    element: <EVChargerMillHillPage />,
  },
  {
    path: '/services/installations/ev-charger/radlett',
    element: <EVChargerRadlettPage />,
  },
  {
    path: '/services/installations/ev-charger/borehamwood',
    element: <EVChargerBorehamwoodPage />,
  },
  {
    path: '/services/installations/ev-charger/elstree',
    element: <EVChargerElstreePage />,
  },
  {
    path: '/services/installations/ev-charger/potters-bar',
    element: <EVChargerPottersBarPage />,
  },
  {
    path: '/services/installations/ev-charger/south-oxhey',
    element: <EVChargerSouthOxheyPage />,
  },
  {
    path: '/services/installations/smart-home',
    element: <SmartHomePage />,
  },
  {
    path: '/services/installations/home-automation',
    element: <HomeAutomationPage />,
  },
  {
    path: '/services/installations/heating-controls',
    element: <HeatingControlsPage />,
  },
  {
    path: '/services/installations/electric-shower',
    element: <ElectricShowerPage />,
  },
  {
    path: '/services/installations/kitchen-electrical',
    element: <KitchenElectricalPage />,
  },
  {
    path: '/services/repairs',
    element: <RepairsPage />,
  },
  {
    path: '/services/repairs/electrical-repairs',
    element: <ElectricalRepairsPage />,
  },
  {
    path: '/services/repairs/fault-finding',
    element: <FaultFindingPage />,
  },
  {
    path: '/services/repairs/tripping-circuits',
    element: <TrippingCircuitsPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs',
    element: <FuseBoxRepairsPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/abbots-langley',
    element: <FuseBoxRepairsAbbotsLangleyPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/borehamwood',
    element: <FuseBoxRepairsBorehamwoodPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/bushey',
    element: <FuseBoxRepairsBusheyPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/chorleywood',
    element: <FuseBoxRepairsChorleywoodPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/croxley-green',
    element: <FuseBoxRepairsCroxleyGreenPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/edgware',
    element: <FuseBoxRepairsEdgwarePage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/elstree',
    element: <FuseBoxRepairsElstreePage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/harrow',
    element: <FuseBoxRepairsHarrowPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/mill-hill',
    element: <FuseBoxRepairsMillHillPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/moor-park-northwood',
    element: <FuseBoxRepairsMoorParkNorthwoodPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/pinner',
    element: <FuseBoxRepairsPinnerPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/potters-bar',
    element: <FuseBoxRepairsPottersBarPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/radlett',
    element: <FuseBoxRepairsRadlettPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/rickmansworth',
    element: <FuseBoxRepairsRickmansworthPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/ruislip',
    element: <FuseBoxRepairsRuislipPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/south-oxhey',
    element: <FuseBoxRepairsSouthOxheyPage />,
  },
  {
    path: '/services/repairs/fuse-box-repairs/stanmore',
    element: <FuseBoxRepairsStanmorePage />,
  },
  {
    path: '/services/repairs/lighting-repairs',
    element: <LightingRepairsPage />,
  },
  {
    path: '/services/repairs/socket-switch-repairs',
    element: <SocketSwitchRepairsPage />,
  },
  {
    path: '/services/repairs/emergency-callouts',
    element: <EmergencyCalloutsPage />,
  },
  {
    path: '/services/repairs/maintenance-contracts',
    element: <MaintenanceContractsPage />,
  },
  {
    path: '/services/repairs/pat-testing',
    element: <PATTestingPage />,
  },
  {
    path: '/services/repairs/appliance-repairs',
    element: <ApplianceRepairsPage />,
  },
  {
    path: '/services/safety-compliance',
    element: <SafetyCompliancePage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing',
    element: <EICRTestingPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/watford-town-centre',
    element: <EICRWatfordTownCentrePage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/oxhey',
    element: <EICROxheyPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/bushey',
    element: <EICRBusheyPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/croxley-green',
    element: <EICRCroxleyGreenPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/abbots-langley',
    element: <EICRAbbotsLangleyPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/rickmansworth',
    element: <EICRRickmansworthPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/garston',
    element: <EICRGarstonPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/north-watford',
    element: <EICRNorthWatfordPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/west-watford',
    element: <EICRWestWatfordPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/carpenders-park',
    element: <EICRCarpendersParkPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/leavesden',
    element: <EICRLeavesdenPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/nascot-wood',
    element: <EICRNascotWoodPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/holywell',
    element: <EICRHolywellPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/meriden',
    element: <EICRMeridenPage />,
  },
  {
    path: '/services/safety-compliance/eicr-testing/cassiobury',
    element: <EICRCassioburyPage />,
  },
  {
    path: '/services/safety-compliance/landlord-safety-certificates',
    element: <LandlordSafetyCertificatesPage />,
  },
  {
    path: '/services/safety-compliance/homebuyer-electrical-reports',
    element: <HomebuyerElectricalReportsPage />,
  },
  {
    path: '/services/safety-compliance/smoke-heat-alarm',
    element: <SmokeHeatAlarmPage />,
  },
  {
    path: '/services/safety-compliance/fire-alarm-systems',
    element: <FireAlarmSystemsPage />,
  },
  {
    path: '/services/safety-compliance/surge-protection',
    element: <SurgeProtectionPage />,
  },
  {
    path: '/services/safety-compliance/earthing-bonding',
    element: <EarthingBondingPage />,
  },
  {
    path: '/services/safety-compliance/rcd-testing',
    element: <RCDTestingPage />,
  },
  // Area Pages
  {
    path: '/electrician-rickmansworth',
    element: <RickmansworthPage />,
  },
  {
    path: '/electrician-in-chorleywood',
    element: <ChorleywoodPage />,
  },
  {
    path: '/electrician-in-moor-park-northwood',
    element: <MoorParkNorthwoodPage />,
  },
  {
    path: '/electrician-croxley-green',
    element: <CroxleyGreenPage />,
  },
  {
    path: '/electrician-pinner',
    element: <PinnerPage />,
  },
  {
    path: '/electrician-bushey',
    element: <BusheyPage />,
  },
  {
    path: '/electrician-abbots-langley',
    element: <AbbotsLangleyPage />,
  },
  {
    path: '/electrician-harrow',
    element: <HarrowPage />,
  },
  {
    path: '/electrician-ruislip',
    element: <RuislipPage />,
  },
  {
    path: '/electrician-stanmore',
    element: <StanmorePage />,
  },
  {
    path: '/electrician-edgware',
    element: <EdgwarePage />,
  },
  {
    path: '/electrician-mill-hill',
    element: <MillHillPage />,
  },
  {
    path: '/electrician-radlett',
    element: <RadlettPage />,
  },
  {
    path: '/electrician-borehamwood',
    element: <BorehamwoodPage />,
  },
  {
    path: '/electrician-elstree',
    element: <ElstreePage />,
  },
  {
    path: '/electrician-potters-bar',
    element: <PottersBarPage />,
  },
  {
    path: '/electrician-south-oxhey',
    element: <SouthOxheyPage />,
  },
  // Electrical Installations Area Pages
  {
    path: '/services/installations/electrical-installations/rickmansworth',
    element: <EIRickmansworthPage />,
  },
  {
    path: '/services/installations/electrical-installations/chorleywood',
    element: <EIChorleywoodPage />,
  },
  {
    path: '/services/installations/electrical-installations/moor-park-northwood',
    element: <EIMoorParkNorthwoodPage />,
  },
  {
    path: '/services/installations/electrical-installations/croxley-green',
    element: <EICroxleyGreenPage />,
  },
  {
    path: '/services/installations/electrical-installations/pinner',
    element: <EIPinnerPage />,
  },
  {
    path: '/services/installations/electrical-installations/bushey',
    element: <EIBusheyPage />,
  },
  {
    path: '/services/installations/electrical-installations/abbots-langley',
    element: <EIAbbotsLangleyPage />,
  },
  {
    path: '/services/installations/electrical-installations/harrow',
    element: <EIHarrowPage />,
  },
  {
    path: '/services/installations/electrical-installations/ruislip',
    element: <EIRuislipPage />,
  },
  {
    path: '/services/installations/electrical-installations/stanmore',
    element: <EIStanmorePage />,
  },
  {
    path: '/services/installations/electrical-installations/edgware',
    element: <EIEdgwarePage />,
  },
  {
    path: '/services/installations/electrical-installations/mill-hill',
    element: <EIMillHillPage />,
  },
  {
    path: '/services/installations/electrical-installations/radlett',
    element: <EIRadlettPage />,
  },
  {
    path: '/services/installations/electrical-installations/borehamwood',
    element: <EIBorehamwoodPage />,
  },
  {
    path: '/services/installations/electrical-installations/elstree',
    element: <EIElstreePage />,
  },
  {
    path: '/services/installations/electrical-installations/potters-bar',
    element: <EIPottersBarPage />,
  },
  {
    path: '/services/installations/electrical-installations/south-oxhey',
    element: <EISouthOxheyPage />,
  },
  // Legacy URL redirects from beyondelectrical.co.uk
  {
    path: '/rewires',
    element: <Navigate to="/services/installations/rewiring" replace />,
  },
  {
    path: '/ev-chargers',
    element: <Navigate to="/services/installations/ev-charger" replace />,
  },
  {
    path: '/fault-finding',
    element: <Navigate to="/services/repairs/fault-finding" replace />,
  },
  {
    path: '/consumer-units-fuse-board',
    element: <Navigate to="/services/installations/consumer-unit-upgrades" replace />,
  },
  {
    path: '/eicr-electrical-installation-condition-report',
    element: <Navigate to="/services/safety-compliance/eicr-testing" replace />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default routes;